import { useEffect, useRef, useState } from 'react';

/**
 * `value`, once it has stopped changing for `delayMs` (spec 208).
 *
 * The bundle check runs by itself as answers change. Checking on every keystroke
 * of a new key would send a request per letter, so the answers it is made with
 * are the ones that have settled.
 *
 * - The first value, and the first value after `resetKey` changes, settle at
 *   once: a newly chosen file is checked without waiting.
 * - A change is a change by `same`, not by identity, so a value rebuilt on every
 *   render does not keep restarting the wait.
 *
 * One timer, restarted on each real change and cleared on unmount. It is a
 * debounce, not the polling loop the constitution rules out.
 */
export function useSettledValue<T>(
  value: T,
  delayMs: number,
  same: (a: T, b: T) => boolean = Object.is,
  resetKey?: unknown,
): T {
  const [settled, setSettled] = useState(value);
  const [settledFor, setSettledFor] = useState(resetKey);
  const latest = useRef(value);
  const latestFor = useRef(resetKey);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // A new subject settles at once (adjusting state during render, so the first
  // render for it already returns the new value).
  const fresh = settledFor !== resetKey;
  if (fresh) {
    setSettledFor(resetKey);
    setSettled(value);
  }

  useEffect(() => {
    if (latestFor.current !== resetKey) {
      latestFor.current = resetKey;
      latest.current = value;
      clearTimeout(timer.current);
      return;
    }
    if (same(value, latest.current)) return;
    latest.current = value;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setSettled(latest.current), delayMs);
  });

  useEffect(() => () => clearTimeout(timer.current), []);

  return fresh ? value : settled;
}
